import { SubmitButton } from "@/components/Global/Button/Button";
import { FormErrorState, Input } from "@/components/Global/Input/Input";
import { Section } from "@/components/Global/Section/Section";
import { useAppContext } from "@/context/AppContext";
import { customSanitize } from "@/utils/customSanitize";
import { createUser } from "@/utils/serviceAPI/user";
import { safeParseSchema, userSchema } from "@/utils/validations";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { useRouter } from "next/router";
import React, { FormEvent, useState } from "react";
import { ExternButton } from "../SharedComponents/ExternButton/ExternButton";
import { LoginNavigation } from "../SharedComponents/LoginNavigation/LoginNavigation";
import { toggleExternalRegister } from "../utils/utils";
import { useModal } from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import { Loader } from "@/components/Loader/Loader";

export type UserRegistrationType = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeat: string;
  policy: boolean;
};

function Registrierung() {
  /* jscpd:ignore-start */
  const { getLabel } = useGlobalContext();
  const { setFormModalText } = useAppContext();
  const { setOpen } = useModal();
  const validationMessages = {
    email_invalid: getLabel("email_invalid"),
    required_field: getLabel("error_required_field"),
    required_checkbox: getLabel("error_checkbox_required"),
    passwords_not_matching: getLabel("passwords_not_matching"),
  };
  const [errors, setErrors] = useState<FormErrorState>();
  const [loading, setLoading] = React.useState(false);
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const formProps = Object.fromEntries(formData);
    const { data, flattenedErrors } = safeParseSchema(
      userSchema(validationMessages),
      formProps,
    );

    if (data) {
      setLoading(true);
      setErrors({});
      // eslint-disable-next-line unused-imports/no-unused-vars
      const { policy, repeat, ...userData } = data as UserRegistrationType;
      try {
        const res = await createUser({
          ...userData,
          profilePicture: "",
          role: "user",
          favouriteFood: "",
          userLocale: router.locale,
          isVerified: false,
        });
        if (res) {
          setOpen(true);
          setFormModalText(
            getLabel("register_form_notification_success").replace(
              "$email",
              `<b>${userData.email}</b>`,
            ),
          );
          setLoading(false);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setOpen(true);
        setFormModalText(err?.response?.data?.message || "Registration failed");
        setLoading(false);
      }
    } else {
      // focus the first error input
      if (flattenedErrors) {
        // get the first error in flattenedErrors
        const firstErrorKey = Object.keys(flattenedErrors)[0];
        const errorInput = document.getElementById(firstErrorKey);
        if (errorInput) {
          errorInput.focus();
          errorInput.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      setErrors(flattenedErrors);
    }
  };
  /* jscpd:ignore-end */
  const router = useRouter();
  return (
    <Section columnsClassName="is-centered is-multiline">
      <div className="column is-10-tablet is-8-desktop pb-0">
        <LoginNavigation
          label={getLabel("login_navigation_label_login")}
          href="/login"
        />
      </div>
      <div className="column is-10-tablet is-8-desktop is-6-widescreen">
        <form onSubmit={handleSubmit} autoComplete="on">
          <p className={`login-area-headline pb-5`}>
            {getLabel("login_form_label_personal")}
          </p>
          <Input
            inputType="text"
            id="firstName"
            name="firstName"
            label={getLabel("login_form_label_first_name")}
            placeholder={getLabel("login_form_placeholder_first_name")}
            errors={errors}
          ></Input>
          <Input
            inputType="text"
            id={"lastName"}
            name="lastName"
            label={getLabel("login_form_label_last_name")}
            placeholder={getLabel("login_form_placeholder_last_name")}
            errors={errors}
          ></Input>
          <Input
            inputType="email"
            id="email"
            name="email"
            label={getLabel("login_form_label_email")}
            placeholder={getLabel("login_form_placeholder_email")}
            errors={errors}
          ></Input>
          <div className="mt-5">
            <span className={`login-area-headline`}>
              {getLabel("login_form_label_password")}
            </span>
            <p>{getLabel("login_form_label_password_notice")}</p>
          </div>
          <Input
            inputType="password"
            id="password"
            name="password"
            label={getLabel("login_form_label_password")}
            placeholder={getLabel("login_form_placeholder_password_set")}
            errors={errors}
            aria-autocomplete="list"
          ></Input>
          <Input
            inputType="password"
            id="repeat"
            name="repeat"
            label={getLabel("login_form_label_password_repeat")}
            placeholder={getLabel("login_form_placeholder_password_repeat")}
            errors={errors}
            aria-autocomplete="list"
            className="mb-5"
          ></Input>
          <Input
            inputType="checkbox"
            id="policy"
            name="policy"
            label_checkbox={getLabel("register_form_checkbox_privacy")}
            errors={errors}
          ></Input>
          <div className="mt-6">
            <SubmitButton className="login-submit-buton">
              {getLabel("register_form_label_button_intern")}
            </SubmitButton>
            <ExternButton
              onClick={(clickEvent: React.MouseEvent<HTMLButtonElement>) => {
                clickEvent.preventDefault();
                toggleExternalRegister("google");
              }}
              label={getLabel("register_form_label_button_google")}
            />
          </div>
          {/* jscpd:ignore-start */}
          {loading && <Loader />}
        </form>
        <p
          className="has-text-centered mt-6"
          dangerouslySetInnerHTML={{
            __html: customSanitize(
              getLabel("login_navigation_button_login_bottom"),
            ),
          }}
        ></p>
      </div>
    </Section>
  );
}
/* jscpd:ignore-end */

export default Registrierung;
